// Ordbog (glossary) registry — single source for the /viden listing and the
// /viden/$slug detail pages, the sitemap and llms.txt. To add a term: append
// an entry here; listing, detail page, alphabet index and sitemap follow.
// Danish-first like the rest of the site; localized fields can be added when
// the Viden section joins the en/de rollout.

import { type Locale } from "@/i18n/config";
import { pathFor, type PageId } from "@/i18n/slugMap";

export type OrdbogSection = { heading?: string; body: string };

export type OrdbogTerm = {
  slug: string;
  term: string;
  /** One-line explanation shown in the listing (keep it under ~125 chars). */
  short: string;
  sections: OrdbogSection[];
  /** Optional deep-link to a related service/page on the site. */
  related?: { pageId: PageId; label: string };
};

export const ordbogTerms: OrdbogTerm[] = [
  {
    slug: "hse",
    term: "HSE",
    short:
      "HSE står for Health, Safety & Environment – det systematiske arbejde med sundhed, sikkerhed og miljø på byggepladsen.",
    sections: [
      {
        body: "HSE er en forkortelse for Health, Safety & Environment – på dansk sundhed, sikkerhed og miljø. Begrebet dækker det systematiske arbejde med at forebygge arbejdsulykker, beskytte medarbejdernes sundhed og minimere påvirkningen af miljøet på bygge- og anlægsprojekter. Forkortelsen ses også som EHS eller SHE – rækkefølgen varierer, men indholdet er det samme.",
      },
      {
        heading: "HSE i praksis på byggepladsen",
        body: "I praksis omfatter HSE-arbejdet blandt andet risikovurderinger, sikkerhedsrunderinger, toolbox talks, granskning af arbejdsprocedurer og koordinering af sikkerheden på tværs af entreprenører. Dertil kommer løbende registrering og opfølgning på hændelser og nærved-ulykker, så der kan sættes ind, før noget går galt.",
      },
      {
        heading: "Hvorfor er HSE vigtigt?",
        body: "På store projekter med mange entreprenører og fag i gang samtidig er en stærk HSE-kultur afgørende. God sikkerhedsledelse forebygger ulykker og nedetid, sikrer at lovkrav og bygherrens egne standarder bliver overholdt – og er i stigende grad et kontraktkrav på komplekse projekter inden for blandt andet pharma, datacentre og industri.",
      },
    ],
    related: { pageId: "hse", label: "Sikkerhedsledelse (HSE) hos MI Support" },
  },
  // 👇 add new terms here
];

/** Full localized path to a term, e.g. /viden/hse. */
export function termPath(t: OrdbogTerm, locale: Locale): string {
  return `${pathFor("viden", locale)}/${t.slug}`;
}

export function findTermBySlug(slug: string): OrdbogTerm | undefined {
  return ordbogTerms.find((t) => t.slug === slug);
}

/** Reverse lookup by full pathname — used by the language switcher. */
export function findTermByPath(pathname: string): OrdbogTerm | undefined {
  return ordbogTerms.find((t) => termPath(t, "da") === pathname);
}

/** Full alphabet for the "Hop til bogstav" index. */
export const ordbogAlphabet = [..."ABCDEFGHIJKLMNOPQRSTUVWXYZÆØÅ"];

/** Terms grouped by first letter, letters and terms sorted alphabetically (da). */
export function termsByLetter(): { letter: string; terms: OrdbogTerm[] }[] {
  const groups = new Map<string, OrdbogTerm[]>();
  for (const t of ordbogTerms) {
    const letter = t.term.charAt(0).toUpperCase();
    groups.set(letter, [...(groups.get(letter) ?? []), t]);
  }
  return [...groups.entries()]
    .map(([letter, terms]) => ({
      letter,
      terms: terms.sort((a, b) => a.term.localeCompare(b.term, "da")),
    }))
    .sort((a, b) => a.letter.localeCompare(b.letter, "da"));
}
